/**
 * Horizontal Displacement + Visual Pattern Recognition
 *
 * Extends hipDisplacementHorizontal with two additional auto-signal layers
 * (forward head posture, tibial inclination) and optional practitioner-toggled
 * soft-tissue observations (pooch belly, flat glutes, rib flare, flat cervical curve).
 *
 * Auto signals raise the max point budget from 5 → 7.  Each toggled practitioner
 * observation adds +1 to the relevant orientation side and increases the confidence
 * denominator accordingly, so un-toggled observations never penalise confidence.
 */

import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import {
  MAX_ORIENTATION_POINTS,
  sagittalFromLandmarks,
  scoreApt,
  scoreSwayback,
} from './hip-displacement-horizontal-strategy';
import {
  logOrientationDecisionDebug,
  logPostureClassificationDebug,
} from './posture-classification-debug';
import {
  HIP_VPR_SIGNAL_UI_MAP,
  HIP_VPR_SWAYBACK_POSSIBLE_SIGNALS,
  HIP_VPR_APT_POSSIBLE_SIGNALS,
} from './posture-classification-debug';

const STRATEGY_ID = 'hipDisplacementHorizontalVisualPatternRecognition';
const DEBUG_LOG_PREFIX = '[hipDisplacementHorizontalVPR]';
const STRATEGY_NAME =
  'Horizontal Displacement + Visual Pattern Recognition (head/trunk signals)';

const MAX_AUTO_POINTS = 7;

interface AugmentedScore {
  pts: number;
  evidence: string[];
  activeSignals: string[];
}

function augmentScores(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext,
): {
  sb: AugmentedScore;
  apt: AugmentedScore;
  dynamicMax: number;
} | null {
  const rules = (getEvaluationRules() as any).posture.thresholds;
  const kneeThresholds = {
    kneeFlexionDegMin: rules.kneeFlexionDegMin,
    kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
  };
  const parsed = parseLateralPostureMetrics(input.analysisData, kneeThresholds);
  if (!parsed) return null;

  const avgCvAngle = parsed.avgCvAngle;
  const tibialInclination = parsed.tibialInclination;
  const pelvicTiltDegMin =
    input.clientSex === 'Female'
      ? rules.pelvicTiltDegMinFemale
      : rules.pelvicTiltDegMinMale;

  // Compute BOTH base raw scores faithfully (not just the winner). This fixes the previous
  // "winner-only seeding" limitation: secondary VPR signals can now lift the losing side and
  // flip a close decision (e.g. 2-vs-3 → 4-vs-3) instead of only padding the winner.
  const fromLm = sagittalFromLandmarks(
    input,
    context,
    kneeThresholds.kneeFlexionDegMin,
    kneeThresholds.kneeHyperextensionDegMin,
  );
  const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
  const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
  const kneeHyperext = fromLm ? fromLm.kneeHyperext : parsed.isKneeHyperext && !parsed.isKneeNeutral;
  const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
  const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
  const kneeFlexEvidenceDeg =
    fromLm?.kneeFlexEvidenceDeg ??
    (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : undefined);
  const kneeHipAntCm =
    fromLm && isFinite(fromLm.kneeHipAntCm) ? fromLm.kneeHipAntCm : parsed.kneeHipAntCm;

  const sbBase = scoreSwayback(
    hipFwdCm,
    torsoOffsetCm,
    kneeFlex,
    pelvicTilt,
    rules,
    kneeFlexEvidenceDeg,
    kneeHipAntCm,
    pelvicTiltDegMin,
  );
  const aptBase = scoreApt(
    hipFwdCm,
    torsoOffsetCm,
    kneeHyperext,
    pelvicTilt,
    rules,
    input.clientSex,
    kneeHipAntCm,
  );

  let sbPts = sbBase.pts;
  let aptPts = aptBase.pts;
  const sbEvidence: string[] = [...sbBase.evidence];
  const aptEvidence: string[] = [...aptBase.evidence];
  const sbSignals: string[] = [...sbBase.activeSignals];
  const aptSignals: string[] = [...aptBase.activeSignals];

  // --- New auto signals: Forward Head Posture ---
  const fhpMin = rules.fhpSwaybackDegMin ?? 10;
  if (isFinite(avgCvAngle) && avgCvAngle >= fhpMin) {
    sbPts += 1;
    sbSignals.push('vpr.sb.forwardHeadPosture');
    sbEvidence.push(
      `[+1] Forward head posture (CV angle ~${avgCvAngle.toFixed(1)}° ≥ ${fhpMin}°; typical swayback presentation)`,
    );
  } else if (
    isFinite(avgCvAngle) &&
    avgCvAngle < fhpMin &&
    isFinite(pelvicTilt) &&
    pelvicTilt >= pelvicTiltDegMin
  ) {
    aptPts += 1;
    aptSignals.push('vpr.apt.neutralHeadPosture');
    aptEvidence.push(
      `[+1] Neutral/mild head posture (CV ~${avgCvAngle.toFixed(1)}° < ${fhpMin}°) with anterior pelvic tilt (~${pelvicTilt.toFixed(1)}°); supports APT presentation`,
    );
  }

  // --- New auto signals: Tibial Inclination ---
  const tibPostMin = rules.tibialPostThresholdDeg ?? 3;
  const tibAntMin = rules.tibialAntThresholdDeg ?? 3;
  if (isFinite(tibialInclination) && tibialInclination <= -tibPostMin) {
    sbPts += 1;
    sbSignals.push('vpr.sb.posteriorTibialInclination');
    sbEvidence.push(
      `[+1] Posterior tibial inclination (~${tibialInclination.toFixed(1)}° ≤ −${tibPostMin}°; knee flexion compensation pattern)`,
    );
  } else if (isFinite(tibialInclination) && tibialInclination >= tibAntMin) {
    aptPts += 1;
    aptSignals.push('vpr.apt.anteriorTibialInclination');
    aptEvidence.push(
      `[+1] Anterior tibial inclination (~${tibialInclination.toFixed(1)}° ≥ ${tibAntMin}°; hyperextension compensation pattern)`,
    );
  }

  // --- Practitioner visual observations (optional toggles) ---
  const obs = input.practitionerObservations;
  let toggledCount = 0;

  if (obs?.poochBelly) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.poochBelly');
    sbEvidence.push('[+1] Practitioner observation: pooch belly (swayback indicator)');
  }
  if (obs?.flatGlutes) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.flatGlutes');
    sbEvidence.push('[+1] Practitioner observation: flat/absent glutes (swayback indicator)');
  }
  if (obs?.ribFlare) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.ribFlare');
    aptEvidence.push('[+1] Practitioner observation: rib flare (APT indicator)');
  }
  if (obs?.flatCervicalCurve) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.flatCervicalCurve');
    aptEvidence.push(
      '[+1] Practitioner observation: flat/reduced cervical curve (APT indicator)',
    );
  }

  const dynamicMax = MAX_AUTO_POINTS + toggledCount;

  return {
    sb: {
      pts: Math.min(dynamicMax, sbPts),
      evidence: sbEvidence,
      activeSignals: sbSignals,
    },
    apt: {
      pts: Math.min(dynamicMax, aptPts),
      evidence: aptEvidence,
      activeSignals: aptSignals,
    },
    dynamicMax,
  };
}

export const hipDisplacementHorizontalVisualPatternRecognitionStrategy: PostureOrientationClassificationStrategy =
  {
    id: STRATEGY_ID,
    displayName: STRATEGY_NAME,
    maxClassificationPoints: MAX_AUTO_POINTS,

    classify(
      input: PostureClassificationStrategyInput,
      context: PostureClassificationStrategyContext,
    ): PostureClassificationStrategyResult | null {
      const augmented = augmentScores(input, context);
      if (!augmented) return null;

      const { sb, apt, dynamicMax } = augmented;

      let orientation: PostureOrientationLabel = 'Stacked';
      let matched = 0;
      let evidence: string[] = [];
      let decisionPath = '';

      const rules = (getEvaluationRules() as any).posture.thresholds;

      if (sb.pts === 0 && apt.pts === 0) {
        orientation = 'Stacked';
        evidence = [
          'No strong swayback vs anterior-orientation separation on current landmarks/metrics.',
        ];
        matched = 0;
        decisionPath = 'noPoints.stacked';
      } else if (sb.pts > apt.pts) {
        orientation = 'Swayback';
        matched = sb.pts;
        evidence = sb.evidence;
        decisionPath = 'sbPointsGreater';
      } else if (apt.pts > sb.pts) {
        orientation = 'Anterior Pelvic Tilt';
        matched = apt.pts;
        evidence = apt.evidence;
        decisionPath = 'aptPointsGreater';
      } else {
        // Tie: use hip displacement as tie-breaker (same as base strategy)
        const parsed = parseLateralPostureMetrics(input.analysisData, {
          kneeFlexionDegMin: rules.kneeFlexionDegMin,
          kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
        });
        const hipFwdCm = parsed?.hipFwdVal ?? 0;

        if (hipFwdCm >= rules.hipForwardCmMin && sb.pts > 0) {
          orientation = 'Swayback';
          matched = sb.pts;
          evidence = [
            ...sb.evidence,
            'Tie-break: large hip–ankle offset favors swayback shift.',
          ];
          decisionPath = 'tieBreak.hipFwdFavorsSwayback';
        } else {
          orientation = 'Anterior Pelvic Tilt';
          matched = apt.pts;
          evidence = [
            ...apt.evidence,
            'Tie-break: hip stack not dominant → anterior orientation.',
          ];
          decisionPath = 'tieBreak.hipStackFavorsApt';
        }
      }

      logOrientationDecisionDebug(DEBUG_LOG_PREFIX, {
        signalUiMap: HIP_VPR_SIGNAL_UI_MAP,
        activeSignals: [...sb.activeSignals, ...apt.activeSignals],
        signalGroups: [
          {
            label: 'swayback',
            prefix: 'vpr.sb.',
            possible: HIP_VPR_SWAYBACK_POSSIBLE_SIGNALS,
          },
          {
            label: 'apt',
            prefix: 'vpr.apt.',
            possible: HIP_VPR_APT_POSSIBLE_SIGNALS,
          },
        ],
        scoreByGroup: { swayback: { pts: sb.pts }, apt: { pts: apt.pts } },
        decisionPath,
        orientation,
        matchedPoints: matched,
        maxPoints: dynamicMax,
        finalEvidence: evidence,
      });

      const confidencePct =
        orientation === 'Stacked'
          ? Math.min(
              100,
              Math.round(
                (1 - Math.max(sb.pts, apt.pts) / Math.max(1, dynamicMax * 2)) *
                  100,
              ),
            )
          : Math.min(100, Math.round((matched / dynamicMax) * 100));

      logPostureClassificationDebug(
        DEBUG_LOG_PREFIX,
        'augmented classification result',
        {
          augmentedOrientation: orientation,
          augmentedConfidence: confidencePct,
          sbPts: sb.pts,
          aptPts: apt.pts,
          dynamicMax,
          practitionerObservations: input.practitionerObservations ?? null,
        },
      );

      return {
        orientation,
        confidencePct,
        matchedPoints: matched,
        maxPoints: dynamicMax,
        strategyId: STRATEGY_ID,
        strategyDisplayName: STRATEGY_NAME,
        evidence,
      };
    },
  };
